import { Trans, useLingui } from '@lingui/react/macro';

import FileUpload from '@/components/FileUpload';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { getRefashionStatus } from '@/models/actionType';
import { getArticlePhoto } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useCurrentSession } from '@/services/auth';

export const ArticlePhotoUpload = ({
  secondaryActionIconOnly,
}: {
  secondaryActionIconOnly?: boolean;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const { request, article, errors, view } = useArticleContext();

  const hasRefashionAction =
    article.snapshot.articleActions?.some(
      (action) => getRefashionStatus(action.organizationPrice) === 'applied'
    ) ?? false;
  const showOptional = !view.details.articlePhoto.required && !hasRefashionAction;

  if (!currentSession) {
    return null;
  }

  let canUpdateArticlePhoto: boolean = false;

  if (request.draft || article.step?.step === 'creation') {
    canUpdateArticlePhoto = request.creatorId === currentSession.id;
  } else if (article.step?.step === 'service-choice' || article.step?.step === 'repair') {
    canUpdateArticlePhoto = !!article.taskType;
  }

  return (
    <FileUpload
      uploadData={{
        type: 'article-photo',
        articleId: article.id,
      }}
      type="photo"
      label={
        <>
          <Trans id="article.form.article-photo.label">Full item picture</Trans>
          {showOptional && canUpdateArticlePhoto && <OptionalLabel />}
        </>
      }
      ariaLabel={t({
        id: 'article.form.article-photo.label',
        message: 'Full item picture',
      })}
      maxNumberOfMedia={1}
      media={getArticlePhoto(article) ? [getArticlePhoto(article)!] : []}
      size="xxlarge"
      disabled={!canUpdateArticlePhoto}
      error={
        errors.details?.missingArticlePhoto || errors.details?.missingArticlePhotoForRefashion
          ? t({
              id: 'requests.articles.form.error.photo',
              message: 'Please add a full item picture',
            })
          : undefined
      }
      deleteWithApi
      secondaryActionIconOnly={secondaryActionIconOnly}
      withQrCode
    />
  );
};
